// loadsHelpers.ts
import { getHoustonDateInfo } from '../../../helper_functions';

export type GroupBy = 'producer' | 'carrier';

/**
 * Destinations that classify a crude load as a "waste load" (per operations:
 * Orla processing or Sandpoint).
 */
export const WASTE_DESTINATIONS = ['orla', 'sandpoint'];

/** True when a crude row's destination is one of the waste destinations. */
export function isWasteDestination(destinationName: any): boolean {
    const s = String(destinationName ?? '').toLowerCase().replace(/\s+/g, '');
    return s !== '' && WASTE_DESTINATIONS.some(w => s.includes(w));
}

export interface DayContext {
    today: string;       // YYYY-MM-DD (Houston)
    tomorrow: string;    // YYYY-MM-DD
    yesterday: string;   // YYYY-MM-DD
    months: string[];    // unique YYYY-MM partitions covering the three days
}

// One row of the "today / tomorrow" completed-vs-outstanding summary.
export interface DayLoadRow {
    group: string;
    totalLoads: number;   // all loads for the day (completed + outstanding + canceled + rejected)
    completed: number;
    outstanding: number;
    canceledLoads: number;
    rejectedLoads: number;
    wasteLoads: number;   // subset of completed/outstanding routed to a waste destination
}

// One row of the "prior day" stats summary. Completion comes from the `status` column
export interface PriorDayRow {
    group: string;
    totalLoads: number;      // all loads for the day (includes Canceled/Rejected)
    completedLoads: number;  // completed by status or notes (see isCompletedPriorDay); matches donut green slice
    totalVolume: number;
    avgBsw: number | null;   // excludes Canceled/Rejected
    incompleteLoads: number; // active load (not Canceled/Rejected) not completed by status or notes
    canceledLoads: number;
    rejectedLoads: number;
}

// Flattened load shape used by the drilldown detail tables.
export interface DisplayLoad {
    id: any;
    ticket_number: string;
    load_date: string;
    producer_name: string;
    carrier_name: string;
    destination_name: string;
    gross_barrels: number;
    net_barrels: number;
    bsw: number | null;
    observed_gravity: number;
    corrected_gravity: number;
    status: string;
    status_notes: string;
}

function ymdFromDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/**
 * Normalize a load-date value to `YYYY-MM-DD`. Handles ISO strings / ISO datetimes,
 * `MM/DD/YYYY`, and `Date` instances. Date-only strings are sliced (not Date-parsed)
 * to avoid timezone shifting. Returns null for empty/unparseable values.
 */
export function toLocalYmd(value: any): string | null {
    if (value === null || value === undefined || value === '') return null;
    if (value instanceof Date) {
        return isNaN(value.getTime()) ? null : ymdFromDate(value);
    }
    const s = String(value).trim();
    if (!s) return null;

    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

    const us = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (us) return `${us[3]}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}`;

    const d = new Date(s);
    return isNaN(d.getTime()) ? null : ymdFromDate(d);
}

/** Houston-anchored today/tomorrow/yesterday plus the month partitions to fetch. */
export function getDayContext(): DayContext {
    const info = getHoustonDateInfo();
    const today = info.todayString;
    const tomorrow = info.tomorrowString;
    const yesterday = info.yesterdayString;
    const months = Array.from(
        new Set([today, tomorrow, yesterday].map(d => d.slice(0, 7)))
    );
    return { today, tomorrow, yesterday, months };
}

/**
 * BSW for a single crude load. It lives at the top level of the schedule row
 * (`row.bsw`), populated on completion — NOT inside delivery_data. Tolerates a
 * trailing '%' and returns null for missing / non-positive readings.
 */
export function loadBsw(row: any): number | null {
    const raw = row?.bsw;
    if (raw === null || raw === undefined || raw === '') return null;
    const n = typeof raw === 'number' ? raw : Number(String(raw).replace('%', '').trim());
    return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Gross barrels for a crude load — `gross_barrels`, the authoritative measured gross the
 * Crude schedule grid displays/edits and its footer sums.
 */
export function loadGross(row: any): number {
    const n = Number(row?.gross_barrels);
    return Number.isFinite(n) ? n : 0;
}

/** Group value for a row, with a stable fallback for blanks. */
export function rowGroupValue(row: any, groupBy: GroupBy): string {
    const v = groupBy === 'producer' ? row?.producer_name : row?.carrier_name;
    return v === null || v === undefined || v === '' ? 'Unknown' : String(v);
}

/** Crude schedule rows expose `load_date`; tolerate `loadDate` just in case. */
export function crudeYmd(row: any): string | null {
    return toLocalYmd(row?.load_date ?? row?.loadDate);
}

/**
 * Whether a load counts as completed. A settled `status === 'Completed'` counts (and
 * covers the case where the notes aren't pulled in); otherwise the free-text
 * `status_notes` are the signal — the status dropdown lags (only flips to "Completed"
 * once the ticket number is entered, so same-day loads sit on "Scheduled" while the
 * notes already read done). Notes priority: "on track" and reject/cancel never count.
 */
export function isCompleted(row: any): boolean {
    if (isCompletedByStatus(row)) return true;      // settled "Completed" — also covers missing notes
    const note = String(row?.status_notes ?? '').toLowerCase();
    if (!note) return false;
    if (note.includes('on track')) return false;   // in-progress, despite "completed"
    if (/reject|cancel/.test(note)) return false;
    // Avoid false positives like "incomplete" / "uncompleted" which contain "complete".
    if (/\b(uncomplete(d)?|incomplete)\b/.test(note)) return false;
    return /\bcomplete(d)?\b/.test(note);
}

/**
 * Same-day cancel / reject detection. Reads the `status` column AND the free-text
 * `status_notes` — mirroring how isCompleted trusts the notes for same-day loads whose
 * status often still lags as "Scheduled". This keeps a load rejected/canceled via notes
 * out of the Outstanding bucket even before the status dropdown is updated.
 */
export function isCanceledLoad(row: any): boolean {
    if (String(row?.status ?? '').trim().toLowerCase() === 'canceled') return true;
    return /cancel/.test(String(row?.status_notes ?? '').toLowerCase());
}
export function isRejectedLoad(row: any): boolean {
    if (String(row?.status ?? '').trim().toLowerCase() === 'rejected') return true;
    return /reject/.test(String(row?.status_notes ?? '').toLowerCase());
}

/** True iff the status dropdown is exactly "Completed". */
export function isCompletedByStatus(row: any): boolean {
    return String(row?.status ?? '').trim() === 'Completed';
}

/**
 * Prior-day completion. Same rule as isCompleted (settled status OR notes); kept as a
 * named alias for readability at the prior-day call sites.
 */
export function isCompletedPriorDay(row: any): boolean {
    return isCompleted(row);
}

/**
 * Completed-vs-outstanding summary for a single day, grouped by producer/carrier.
 * Waste loads are the subset of crude loads routed to a waste destination
 * (Orla / Sandpoint) — see isWasteDestination.
 */
export function aggregateDayLoads(
    crudeRows: any[],
    ymd: string,
    groupBy: GroupBy
): DayLoadRow[] {
    const map = new Map<string, DayLoadRow>();
    const ensure = (key: string): DayLoadRow => {
        let row = map.get(key);
        if (!row) {
            row = { group: key, totalLoads: 0, completed: 0, outstanding: 0, canceledLoads: 0, rejectedLoads: 0, wasteLoads: 0 };
            map.set(key, row);
        }
        return row;
    };

    for (const r of crudeRows) {
        if (crudeYmd(r) !== ymd) continue;
        const row = ensure(rowGroupValue(r, groupBy));
        row.totalLoads += 1;
        if (isCanceledLoad(r)) { row.canceledLoads += 1; continue; }
        if (isRejectedLoad(r)) { row.rejectedLoads += 1; continue; }
        if (isCompleted(r)) row.completed += 1;
        else row.outstanding += 1; // scheduled / in progress
        if (isWasteDestination(r?.destination_name)) row.wasteLoads += 1;
    }

    return Array.from(map.values()).sort(
        (a, b) => b.totalLoads - a.totalLoads || a.group.localeCompare(b.group)
    );
}

// Day totals across all groups — feeds the KPI stat cards at the top of Panel A.
export interface DayTotals {
    totalLoads: number;
    completed: number;
    outstanding: number;
    wasteLoads: number;
    canceledLoads: number;     // status === 'Canceled' — in totalLoads; shown separately in the donut legend
    rejectedLoads: number;     // status === 'Rejected' — in totalLoads; shown separately in the donut legend
    unassignedCarrier: number; // outstanding crude loads with no carrier assigned
}

/** Ungrouped totals for a single day. totalLoads counts every scheduled load (incl.
 *  Canceled/Rejected); Canceled and Rejected are also tallied separately for the donut
 *  legend, and kept out of completed/outstanding/waste. */
export function summarizeDay(crudeRows: any[], ymd: string): DayTotals {
    const totals: DayTotals = {
        totalLoads: 0,
        completed: 0,
        outstanding: 0,
        wasteLoads: 0,
        canceledLoads: 0,
        rejectedLoads: 0,
        unassignedCarrier: 0,
    };

    for (const r of crudeRows) {
        if (crudeYmd(r) !== ymd) continue;
        totals.totalLoads += 1;
        if (isCanceledLoad(r)) { totals.canceledLoads += 1; continue; }
        if (isRejectedLoad(r)) { totals.rejectedLoads += 1; continue; }
        if (isCompleted(r)) {
            totals.completed += 1;
        } else {
            totals.outstanding += 1;
            const carrier = r?.carrier_name;
            if (carrier === null || carrier === undefined || String(carrier).trim() === '') {
                totals.unassignedCarrier += 1;
            }
        }
        if (isWasteDestination(r?.destination_name)) totals.wasteLoads += 1;
    }

    return totals;
}

// Ungrouped prior-day totals — feeds the KPI stat cards + donut on Panel B.
export interface PriorDayTotals {
    totalLoads: number;
    completedLoads: number;
    totalVolume: number;       // gross_barrels over ALL loads (incl. Canceled/Rejected) — matches the Crude schedule footer
    avgBsw: number | null;
    incompleteLoads: number;   // active (non-Canceled/Rejected) loads not completed by status or notes
    canceledLoads: number;
    rejectedLoads: number;
}

/**
 * Ungrouped prior-day totals. Completion is status-OR-notes (isCompletedPriorDay) so
 * loads already marked "Completed" in the notes count even before the ticket/data is
 * entered.
 */
export function summarizePriorDay(crudeRows: any[], ymd: string): PriorDayTotals {
    let totalLoads = 0, completedLoads = 0, totalVolume = 0, bswSum = 0, bswCount = 0, incompleteLoads = 0, canceledLoads = 0, rejectedLoads = 0;
    for (const r of crudeRows) {
        if (crudeYmd(r) !== ymd) continue;
        totalLoads += 1;
        totalVolume += loadGross(r); // above the skip: canceled/rejected still count toward volume
        if (isCanceledLoad(r)) { canceledLoads += 1; continue; }
        if (isRejectedLoad(r)) { rejectedLoads += 1; continue; }
        const bsw = loadBsw(r);
        if (bsw !== null) { bswSum += bsw; bswCount += 1; }
        if (isCompletedPriorDay(r)) completedLoads += 1;
        else incompleteLoads += 1;
    }
    return {
        totalLoads,
        completedLoads,
        totalVolume,
        avgBsw: bswCount > 0 ? bswSum / bswCount : null,
        incompleteLoads,
        canceledLoads,
        rejectedLoads,
    };
}

/**
 * Prior-day stats grouped by producer/carrier
 */
export function aggregatePriorDay(
    crudeRows: any[],
    ymd: string,
    groupBy: GroupBy
): PriorDayRow[] {
    interface Acc { group: string; totalLoads: number; completedLoads: number; totalVolume: number; bswSum: number; bswCount: number; incompleteLoads: number; canceledLoads: number; rejectedLoads: number; }
    const map = new Map<string, Acc>();
    const ensure = (key: string): Acc => {
        let acc = map.get(key);
        if (!acc) {
            acc = { group: key, totalLoads: 0, completedLoads: 0, totalVolume: 0, bswSum: 0, bswCount: 0, incompleteLoads: 0, canceledLoads: 0, rejectedLoads: 0 };
            map.set(key, acc);
        }
        return acc;
    };

    for (const r of crudeRows) {
        if (crudeYmd(r) !== ymd) continue;
        const acc = ensure(rowGroupValue(r, groupBy));
        acc.totalLoads += 1;
        acc.totalVolume += loadGross(r); // above the skip: canceled/rejected still count toward volume
        if (isCanceledLoad(r)) { acc.canceledLoads += 1; continue; }
        if (isRejectedLoad(r)) { acc.rejectedLoads += 1; continue; }
        const bsw = loadBsw(r);
        if (bsw !== null) { acc.bswSum += bsw; acc.bswCount += 1; }
        if (isCompletedPriorDay(r)) acc.completedLoads += 1;
        else acc.incompleteLoads += 1;
    }

    return Array.from(map.values())
        .map(a => ({
            group: a.group,
            totalLoads: a.totalLoads,
            completedLoads: a.completedLoads,
            totalVolume: a.totalVolume,
            avgBsw: a.bswCount > 0 ? a.bswSum / a.bswCount : null,
            incompleteLoads: a.incompleteLoads,
            canceledLoads: a.canceledLoads,
            rejectedLoads: a.rejectedLoads,
        }))
        .sort((a, b) => b.totalVolume - a.totalVolume || a.group.localeCompare(b.group));
}

/** Flatten a crude schedule row into the drilldown display shape. */
export function toDisplayLoad(r: any): DisplayLoad {
    const bsw = loadBsw(r);
    return {
        id: r?.id,
        ticket_number: r?.ticket_number ?? '',
        load_date: toLocalYmd(r?.load_date) ?? String(r?.load_date ?? ''),
        producer_name: r?.producer_name ?? '',
        carrier_name: r?.carrier_name ?? '',
        destination_name: r?.destination_name ?? '',
        gross_barrels: loadGross(r),
        net_barrels: Number(r?.net_barrels) || 0,
        bsw,
        observed_gravity: Number(r?.observed_gravity) || 0,
        corrected_gravity: Number(r?.corrected_gravity) || 0,
        status: r?.status ?? '',
        status_notes: r?.status_notes ?? '',
    };
}

/** Individual crude loads for a given day + group, ready for a detail SQLTable. */
export function filterDayLoads(
    crudeRows: any[],
    ymd: string,
    groupBy: GroupBy,
    group: string
): DisplayLoad[] {
    return crudeRows
        .filter(r => crudeYmd(r) === ymd && rowGroupValue(r, groupBy) === group)
        .map(toDisplayLoad);
}

/** Active (non-canceled/rejected) loads for a day matching a predicate, as DisplayLoad rows. */
function filterDayLoadsBy(crudeRows: any[], ymd: string, predicate: (r: any) => boolean): DisplayLoad[] {
    return crudeRows
        .filter(r => crudeYmd(r) === ymd && !isCanceledLoad(r) && !isRejectedLoad(r) && predicate(r))
        .map(toDisplayLoad);
}

/** Active loads for a day that are completed (see isCompleted). */
export const filterCompletedLoads = (crudeRows: any[], ymd: string): DisplayLoad[] =>
    filterDayLoadsBy(crudeRows, ymd, isCompleted);

/** Active loads for a day that are not yet completed (the "outstanding" ones). */
export const filterOutstandingLoads = (crudeRows: any[], ymd: string): DisplayLoad[] =>
    filterDayLoadsBy(crudeRows, ymd, r => !isCompleted(r));

/** Active loads for a day routed to a waste destination (see isWasteDestination). */
export const filterWasteLoads = (crudeRows: any[], ymd: string): DisplayLoad[] =>
    filterDayLoadsBy(crudeRows, ymd, r => isWasteDestination(r?.destination_name));

/** Terminal loads for a day (status column or notes) — feed the clickable donut-legend
 *  drilldowns. Canceled takes precedence over Rejected, matching summarizeDay. */
export const filterCanceledLoads = (crudeRows: any[], ymd: string): DisplayLoad[] =>
    crudeRows.filter(r => crudeYmd(r) === ymd && isCanceledLoad(r)).map(toDisplayLoad);

export const filterRejectedLoads = (crudeRows: any[], ymd: string): DisplayLoad[] =>
    crudeRows.filter(r => crudeYmd(r) === ymd && !isCanceledLoad(r) && isRejectedLoad(r)).map(toDisplayLoad);

/** Prior-day active (non-Canceled/Rejected) loads not completed by status or notes. Matches the Uncompleted count. */
export const filterUncompletedLoads = (crudeRows: any[], ymd: string): DisplayLoad[] =>
    crudeRows
        .filter(r => crudeYmd(r) === ymd
            && !isCanceledLoad(r) && !isRejectedLoad(r) && !isCompletedPriorDay(r))
        .map(toDisplayLoad);

/** Prior-day completed loads (status or notes) — the green donut slice. */
export const filterPriorCompletedLoads = (crudeRows: any[], ymd: string): DisplayLoad[] =>
    crudeRows
        .filter(r => crudeYmd(r) === ymd
            && !isCanceledLoad(r) && !isRejectedLoad(r) && isCompletedPriorDay(r))
        .map(toDisplayLoad);
